import { AlertTriangleIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { CHANNEL_LABELS } from '@/components/destinations/utils';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { useProviderServiceDestinationsUpdateMutation } from '@/services/api/generated/providerDestinationsService';
import type { TreatmentRecordTypes } from '@/services/api/generated/treatmentRecordRequestsService';
import {
  DestinationChannelType,
  type DestinationScope,
  EvidenceSourceType,
  LEGACY_SCOPE_MAP,
  type ResendReason,
  SaveMode,
} from '@/types/provider-destinations';

import { resolveDestinationContactPointId } from './destinationContactPoint';
import { ResendReasonSelector } from './ResendReasonSelector';
import { useResendRequest } from './useResendRequest';

interface ResendRequestModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  providerId: string;
  requestId: string;
  providerServiceId?: string | null;
  recordType?: string;
  currentScope?: string;
  currentChannelType?: string;
  currentChannelValue?: string;
}

const CHANNEL_TYPE_MAP: Record<string, DestinationChannelType> = {
  fax: DestinationChannelType.FAX,
  email: DestinationChannelType.EMAIL,
  mail: DestinationChannelType.MAIL,
  portal: DestinationChannelType.PORTAL,
};

const getChannelLabel = (channelType: string | undefined): string => {
  if (!channelType) return 'Unknown';
  const mapped = CHANNEL_TYPE_MAP[channelType.toLowerCase()];
  if (mapped) return CHANNEL_LABELS[mapped];
  return channelType;
};

export const ResendRequestModal = ({
  open,
  onClose,
  matterId,
  providerId,
  requestId,
  providerServiceId,
  recordType,
  currentScope,
  currentChannelType,
  currentChannelValue,
}: ResendRequestModalProps) => {
  const { resend, isResending } = useResendRequest({
    matterId,
    providerId,
    requestId,
  });
  const [setDestination] = useProviderServiceDestinationsUpdateMutation();

  const [isDestinationChange, setIsDestinationChange] = useState(false);
  const [newChannelType, setNewChannelType] =
    useState<DestinationChannelType | null>(null);
  const [newChannelValue, setNewChannelValue] = useState('');
  const [reason, setReason] = useState<ResendReason | null>(null);
  const [notes, setNotes] = useState('');

  const resendScope = recordType
    ? LEGACY_SCOPE_MAP.toScope(recordType as TreatmentRecordTypes)
    : undefined;

  const resetForm = useCallback(() => {
    setIsDestinationChange(false);
    setNewChannelType(null);
    setNewChannelValue('');
    setReason(null);
    setNotes('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleToggleDestinationChange = useCallback(() => {
    setIsDestinationChange((prev) => !prev);
    setReason(null);
  }, []);

  const canSubmit =
    reason !== null &&
    (!isDestinationChange ||
      (Boolean(providerServiceId) &&
        Boolean(resendScope) &&
        newChannelType !== null &&
        newChannelValue.trim().length > 0));

  const handleSubmit = useCallback(async () => {
    if (!reason || !canSubmit) return;

    let newDestinationContactPointId: string | undefined;

    if (isDestinationChange) {
      if (!providerServiceId || !resendScope || !newChannelType) {
        toast.error('Cannot change destination for this request');
        return;
      }

      try {
        const response = await setDestination({
          providerServiceId,
          patchedProviderServiceDestinationsPatchRequest: {
            scope: resendScope as DestinationScope,
            save_mode: SaveMode.SAVE_FOR_FUTURE,
            manual_destination: {
              channel_type: newChannelType,
              value: newChannelValue.trim(),
            },
            evidence: {
              source_type: EvidenceSourceType.USER,
              note: notes.trim() || undefined,
            },
          },
        }).unwrap();

        const contactPointId = resolveDestinationContactPointId({
          response,
          scope: resendScope,
          channelType: newChannelType,
          channelValue: newChannelValue,
        });
        if (!contactPointId) {
          toast.error('Failed to resolve destination contact point');
          return;
        }
        newDestinationContactPointId = contactPointId;
      } catch {
        toast.error('Failed to set new destination');
        return;
      }
    }

    try {
      await resend({
        resend_reason: reason,
        note: notes.trim() || undefined,
        new_destination_contact_point_id: newDestinationContactPointId,
      });
      handleClose();
    } catch {
      // Error toast is handled in useResendRequest.
    }
  }, [
    canSubmit,
    handleClose,
    isDestinationChange,
    newChannelType,
    newChannelValue,
    notes,
    providerServiceId,
    reason,
    resend,
    resendScope,
    setDestination,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      size="xl"
      nested
      zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
    >
      <DialogTitle>Resend Request</DialogTitle>

      <DialogBody>
        <div className="space-y-6">
          {currentScope && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Record Type
              </p>
              <p className="mt-1 text-sm font-medium text-zinc-900">
                {currentScope}
              </p>
            </div>
          )}

          {(currentChannelType || currentChannelValue) && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Current Destination
              </p>
              <div className="mt-1 flex items-center gap-2">
                {currentChannelType && (
                  <span className="inline-flex items-center rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-medium text-zinc-700">
                    {getChannelLabel(currentChannelType)}
                  </span>
                )}
                {currentChannelValue && (
                  <span className="font-mono text-sm text-zinc-900">
                    {currentChannelValue}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            {isDestinationChange ? (
              <Button
                type="button"
                color="amber"
                className="text-sm"
                onClick={handleToggleDestinationChange}
              >
                Sending to different destination
              </Button>
            ) : (
              <Button
                type="button"
                outline
                className="text-sm"
                onClick={handleToggleDestinationChange}
              >
                Change destination
              </Button>
            )}
          </div>

          {isDestinationChange && (
            <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">
                  This will mark the previous destination as incorrect for this
                  scope
                </p>
              </div>

              {(!providerServiceId || !resendScope) && (
                <p className="text-xs text-amber-700">
                  Destination change requires a linked provider destination.
                </p>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Channel Type</Label>
                  <select
                    className="mt-1 block w-full rounded-lg border border-zinc-950/10 bg-transparent px-3 py-1.5 text-sm text-zinc-950"
                    value={newChannelType ?? ''}
                    onChange={(e) =>
                      setNewChannelType(e.target.value as DestinationChannelType)
                    }
                  >
                    <option value="">Select channel...</option>
                    {Object.entries(CHANNEL_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <Label>Destination Value</Label>
                  <Input
                    type="text"
                    placeholder="e.g. fax number, email..."
                    value={newChannelValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewChannelValue(e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
          )}

          <ResendReasonSelector
            isDestinationChange={isDestinationChange}
            value={reason}
            onChange={setReason}
          />

          <Field>
            <Label>Additional Notes (optional)</Label>
            <Textarea
              placeholder="Any additional context for this resend..."
              rows={3}
              resizable={false}
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setNotes(e.target.value)
              }
            />
          </Field>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          type="button"
          outline
          onClick={handleClose}
          disabled={isResending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="indigo-steel"
          onClick={handleSubmit}
          disabled={!canSubmit || isResending}
        >
          {isResending ? (
            <>
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              Resending...
            </>
          ) : (
            'Resend'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
